import { useI18n } from "@baton/i18n";
import { cn } from "../../../cn.ts";
import { detectPlatform, shortcutSpoken, shortcutText } from "../Kbd/keys.ts";

/**
 * § Key hints (2026-10-15, by the owner's decision, FR-015-195): the one look a shortcut hint has,
 * the look `Button`'s `shortcut` has had since the QA call of 2026-09-28 ("keybinds should not be a
 * separate thing, they should always be inside of a button"): quiet text in the platform's glyphs at
 * 70 percent opacity of the text around it, with no cap, "Search ⌘K" or "Search Ctrl+K". Keys held
 * together are written as the platform writes them; a chord, keys pressed one after another, is its
 * steps with the word "then" between them, "Go to inbox G then I"; a letter is shown upper case, as a
 * keyboard prints it. The glyphs are hidden from assistive technology, and `spoken` is the keys spelled
 * out for the control's accessible name or description ("G then I"), which the host places after its
 * words. `Button` draws it after its label, `Tooltip` after its words and `IconButton` through its
 * tooltip, so a button, a tooltip and the `?` dialog cannot disagree on a key's look.
 *
 * The 70 percent holds 4.5:1 only where the text around it has room above the floor: the tooltip's
 * inverse ink, a secondary or ghost button's primary ink. Where the host's ink stands at the floor on
 * its fill, a filled, tinted or danger button's label, the host asks for `ink="label"` and the hint
 * takes that ink whole, quiet by its regular weight beside the label's medium (2026-10-23).
 */
export interface ShortcutHintProps {
  /** The keys as the shortcut registry writes them: "Mod+K", "g i", "?". */
  readonly keys: string;
  /** "quiet", 70 percent of the text around it, unless that text stands at the 4.5:1 floor on its fill, where the host asks for "label", its whole ink. */
  readonly ink?: "quiet" | "label";
  readonly className?: string;
}

/** The hint's keys spelled out for assistive technology: "G then I", "Ctrl+Enter". */
export function useShortcutSpoken(): (keys: string) => string {
  const { t } = useI18n();
  return (keys) => shortcutSpoken(keys, t("ui.shortcut.then"));
}

export function ShortcutHint({ keys, ink = "quiet", className }: ShortcutHintProps) {
  const { t } = useI18n();
  return (
    <span data-slot="shortcut-hint" data-ink={ink} className={cn("inline-flex font-normal tracking-wide", ink === "quiet" && "opacity-70", className)} aria-hidden="true">
      {shortcutText(keys, detectPlatform(navigator.userAgent), t("ui.shortcut.then"))}
    </span>
  );
}
